import type { ButtonColor, ButtonVariant } from "~/atoms/buttons/Button";
import type { Notification } from "~/types";

import { toWholeCurrency } from "./currency";
import { shortDataAtTime } from "./date";
import {
  formatAddress,
  formatCurrency,
  formatUsersName,
  slugToString,
} from "./formatting";
import getCategoryLabel from "./getCategoryLabel";

interface NotificationPill {
  color: "red" | "green" | "blue" | "gray" | "yellow";
  text: string;
}

interface NotificationAction {
  color: ButtonColor;
  variant: ButtonVariant;
  text: string;
  href?: string;
}

interface NotificationEvent {
  event: string;
  pill: NotificationPill;
  action: NotificationAction;
  description?: string;
}

interface FormattedNotification extends NotificationEvent {
  id: string;
  createdAtIso: string;
  createdAtHuman: string;
  description: string;
}

export const supportedNotificationEvents: NotificationEvent[] = [
  {
    event: "invitation:invited",
    description: "Invitation to view a property received.",
    pill: {
      color: "green",
      text: "Invitation Received",
    },
    action: {
      color: "primary",
      variant: "solid",
      text: "View Invitation",
      href: "/tenant/invitations/",
    },
  },
  {
    event: "invitation:accepted",
    pill: {
      color: "green",
      text: "Invitation Accepted",
    },
    action: {
      color: "primary",
      variant: "solid",
      text: "View Person",
    },
  },
  {
    event: "invitation:declined",
    pill: {
      color: "red",
      text: "Invitation Declined",
    },
    action: {
      color: "primary",
      variant: "solid",
      text: "View Person",
    },
  },
  {
    event: "invoice:received",
    pill: {
      color: "green",
      text: "Invoice Received",
    },
    action: {
      color: "primary",
      variant: "solid",
      text: "View Invoice",
    },
  },
  {
    event: "invoice:payment-marked-sent",
    pill: {
      color: "green",
      text: "Payment Marked Sent",
    },
    action: {
      color: "primary",
      variant: "solid",
      text: "Confirm",
    },
  },
  {
    event: "invoice:payment-marked-not-received",
    pill: {
      color: "red",
      text: "Payment Not Received",
    },
    action: {
      color: "primary",
      variant: "solid",
      text: "View Invoice",
    },
  },
  {
    event: "invoice:payment-overdue",
    pill: {
      color: "red",
      text: "Payment Overdue",
    },
    action: {
      color: "primary",
      variant: "solid",
      text: "View Invoice",
    },
  },
  {
    event: "payment:succeeded",
    pill: {
      color: "green",
      text: "Payment Received",
    },
    action: {
      color: "primary",
      variant: "solid",
      text: "View Invoice",
    },
  },
  {
    event: "payment:failed",
    pill: {
      color: "red",
      text: "Payment Failed",
    },
    action: {
      color: "primary",
      variant: "solid",
      text: "View Invoice",
    },
  },
  {
    event: "refund:requires-action",
    pill: {
      color: "yellow",
      text: "Action Required",
    },
    action: {
      color: "primary",
      variant: "solid",
      text: "View Invoice",
    },
  },
  {
    event: "refund:succeeded",
    pill: {
      color: "green",
      text: "Refund Complete",
    },
    action: {
      color: "primary",
      variant: "solid",
      text: "View Invoice",
    },
  },
  {
    event: "refund:failed",
    pill: {
      color: "red",
      text: "Refund Failed",
    },
    action: {
      color: "primary",
      variant: "solid",
      text: "View Invoice",
    },
  },
  {
    event: "condition-report:requested",
    pill: {
      color: "blue",
      text: "New Request",
    },
    action: {
      color: "primary",
      variant: "solid",
      text: "View Report",
    },
  },
  {
    event: "condition-report:completed",
    pill: {
      color: "green",
      text: "Request Complete",
    },
    action: {
      color: "primary",
      variant: "solid",
      text: "View Report",
    },
  },
  {
    event: "maintenance-request:created",
    pill: {
      color: "blue",
      text: "New Request",
    },
    action: {
      color: "primary",
      variant: "solid",
      text: "View Request",
    },
  },
  {
    event: "maintenance-request:updated",
    pill: {
      color: "gray",
      text: "Request Updated",
    },
    action: {
      color: "primary",
      variant: "solid",
      text: "View Request",
    },
  },
  {
    event: "comment:created",
    pill: {
      color: "blue",
      text: "New Comment",
    },
    action: {
      color: "primary",
      variant: "solid",
      text: "View Request",
    },
  },
];

/**
 * @function isNotificationValid
 * @param notification
 * @description Determines if a notification is valid.
 * @returns {boolean} Whether the notification is valid.
 */
export const isNotificationValid = (
  notification: Notification | null | undefined
): boolean => {
  if (!notification?.id) {
    return false;
  }

  return supportedNotificationEvents.some(
    (eventConfig) => eventConfig.event === notification.event
  );
};

/**
 * @function notificationFormatter
 * @param notification
 * @description Formats a notification object.
 * @returns {object} The formatted notification.
 *      {string} id - The notification id.
 *      {string} createdAtIso - The notification creation date.
 *      {string} createdAt - The notification creation date formatted.
 *      {object} pill - The notification pill.
 *      {string} pill.color - The notification pill color.
 *      {string} pill.text - The notification pill text.
 *      {string} description - The html description including spans for styling.
 *      {object} action - The action button.
 *      {string} action.color - The action button type (primary, secondary, tertiary).
 *      {string} action.variant - The action button variant (filled, outlined).
 *      {string} action.text - The action button text.
 *      {function} action.href - The action button href.
 */
const notificationFormatter = (
  notification: Notification
): FormattedNotification | null => {
  const isValidNotification = isNotificationValid(notification);

  if (!isValidNotification) {
    return null;
  }

  const event = supportedNotificationEvents.find(
    (event) => event.event === notification.event
  );

  if (!event) {
    return null;
  }

  const boldStyle = "text-gray-900 font-semibold";

  const createdAt = new Date(notification.createdAt);

  const eventUserType = notification?.category;

  const baseEvent: FormattedNotification = {
    ...event,
    id: notification.id,
    createdAtIso: createdAt.toISOString(),
    createdAtHuman: shortDataAtTime(createdAt),
    description: event.description || "",
  };

  if (
    event.event === "invitation:declined" ||
    event.event === "invitation:accepted"
  ) {
    if (typeof notification?.invitation === "string") {
      return null;
    }

    if (typeof notification?.invitation?.organizationPermission === "string") {
      return null;
    }

    if (
      typeof notification?.invitation?.organizationPermission?.contact ===
      "string"
    ) {
      return null;
    }

    const tenantId =
      notification?.invitation?.organizationPermission?.contact?.id;
    const tenantFirstName =
      notification?.invitation?.organizationPermission?.contact?.firstName;
    const tenantLastName =
      notification?.invitation?.organizationPermission?.contact?.lastName;

    if (!tenantFirstName || !tenantLastName || !tenantId) {
      return null;
    }

    if (event.event === "invitation:accepted") {
      baseEvent.description = `<span class="${boldStyle}">${formatUsersName({ firstName: tenantFirstName, lastName: tenantLastName })}</span> accepted an invitation to view a property.`;
      baseEvent.action.href = `/landlord/people/tenants/${tenantId}/`;
    }

    if (event.event === "invitation:declined") {
      baseEvent.description = `<span class="${boldStyle}">${formatUsersName({ firstName: tenantFirstName, lastName: tenantLastName })}</span> declined an invitation to view a property.`;
      baseEvent.action.href = `/landlord/people/tenants/${tenantId}/`;
    }
  }

  if (
    event.event === "invoice:received" ||
    event.event === "invoice:payment-marked-sent" ||
    event.event === "invoice:payment-marked-not-received" ||
    event.event === "invoice:payment-overdue" ||
    event.event === "payment:succeeded" ||
    event.event === "payment:failed"
  ) {
    if (typeof notification?.invoice === "string") {
      return null;
    }

    const invoiceId = notification?.invoice?.id;
    const invoiceRef = notification?.invoice?.reference;
    const invoiceCategorySlug = notification?.invoice?.category;

    const invoiceCategory = invoiceCategorySlug
      ? getCategoryLabel(invoiceCategorySlug)
      : "Invoice";

    const paymentSentBy =
      notification?.invoice?.paymentSentNotification?.method;

    if (event.event === "invoice:received") {
      if (invoiceCategorySlug === "other" || !invoiceCategorySlug) {
        baseEvent.description = `<span class="${boldStyle}">Invoice #${invoiceRef}</span> has been received.`;
      } else {
        baseEvent.description = `<span class="${boldStyle}">Invoice #${invoiceRef}</span> for ${invoiceCategory} has been received.`;
      }
      baseEvent.action.href = `/tenant/invoices/${invoiceId}/`;
    }

    if (event.event === "invoice:payment-marked-sent") {
      if (typeof notification?.invoice === "string") {
        return null;
      }

      baseEvent.description = `<span class="${boldStyle}">Invoice #${invoiceRef}</span> has been marked as paid via <span class="${boldStyle}">${paymentSentBy}</span>.`;
      baseEvent.action.href = `/landlord/invoices/${invoiceId}/`;
    }

    if (event.event === "invoice:payment-marked-not-received") {
      baseEvent.description = `Payment via ${paymentSentBy} has not been received for <span class="${boldStyle}">Invoice #${invoiceRef}</span>.`;
      baseEvent.action.href = `/tenant/invoices/${invoiceId}/`;
    }

    if (event.event === "invoice:payment-overdue") {
      baseEvent.description = `Payment for <span class="${boldStyle}">Invoice #${invoiceRef}</span> is overdue.`;

      if (eventUserType === "tenant") {
        baseEvent.action.href = `/tenant/invoices/${invoiceId}/`;
      }

      if (eventUserType === "organization") {
        baseEvent.action.href = `/landlord/invoices/${invoiceId}/`;
      }
    }

    if (event.event === "payment:succeeded") {
      baseEvent.description = `Payment for <span class="${boldStyle}">Invoice #${invoiceRef}</span> received.`;

      if (eventUserType === "tenant") {
        baseEvent.action.href = `/tenant/invoices/${invoiceId}/`;
      }

      if (eventUserType === "organization") {
        baseEvent.action.href = `/landlord/invoices/${invoiceId}/`;
      }
    }

    if (event.event === "payment:failed") {
      baseEvent.description = `Payment for <span class="${boldStyle}">Invoice #${invoiceRef}</span> failed.`;

      if (eventUserType === "tenant") {
        baseEvent.action.href = `/tenant/invoices/${invoiceId}/`;
      }

      if (eventUserType === "organization") {
        baseEvent.action.href = `/landlord/invoices/${invoiceId}/`;
      }
    }
  }

  if (
    event.event === "refund:requires-action" ||
    event.event === "refund:succeeded" ||
    event.event === "refund:failed"
  ) {
    if (typeof notification?.refund === "string") {
      return null;
    }

    const invoiceId =
      typeof notification?.refund?.invoice !== "string"
        ? notification?.refund?.invoice?.id
        : null;
    const refundAmount = notification?.refund?.amount || 0;
    const invoiceRef =
      typeof notification?.refund?.invoice !== "string"
        ? notification?.refund?.invoice?.reference
        : null;

    if (event.event === "refund:requires-action") {
      baseEvent.description = `Action needed for refund of <span class="${boldStyle}">${formatCurrency(toWholeCurrency(refundAmount))}</span> for <span class="${boldStyle}">Invoice #${invoiceRef}</span>`;

      if (eventUserType === "tenant") {
        baseEvent.action.href = `/tenant/invoices/${invoiceId}/`;
      }

      if (eventUserType === "organization") {
        baseEvent.action.href = `/landlord/invoices/${invoiceId}/`;
      }
    }

    if (event.event === "refund:succeeded") {
      baseEvent.description = `Refund of <span class="${boldStyle}">${formatCurrency(toWholeCurrency(refundAmount))}</span> for <span class="${boldStyle}">Invoice #${invoiceRef}</span> has been processed.`;

      if (eventUserType === "tenant") {
        baseEvent.action.href = `/tenant/invoices/${invoiceId}/`;
      }

      if (eventUserType === "organization") {
        baseEvent.action.href = `/landlord/invoices/${invoiceId}/`;
      }
    }

    if (event.event === "refund:failed") {
      baseEvent.description = `Refund of <span class="${boldStyle}">${formatCurrency(toWholeCurrency(refundAmount))}</span> for <span class="${boldStyle}">Invoice #${invoiceRef}</span> has failed.`;

      if (eventUserType === "tenant") {
        baseEvent.action.href = `/tenant/invoices/${invoiceId}/`;
      }

      if (eventUserType === "organization") {
        baseEvent.action.href = `/landlord/invoices/${invoiceId}/`;
      }
    }
  }

  if (
    event.event === "condition-report:requested" ||
    event.event === "condition-report:completed"
  ) {
    if (typeof notification?.conditionReport === "string") {
      return null;
    }

    if (typeof notification?.conditionReport?.unit === "string") {
      return null;
    }

    if (typeof notification?.conditionReport?.lease === "string") {
      return null;
    }

    const conditionReportId = notification?.conditionReport?.id;
    const unitAddress = notification?.conditionReport?.unit?.address;
    const reportType = slugToString(notification?.conditionReport?.type || "");
    const leaseId = notification?.conditionReport?.lease?.id;

    if (event.event === "condition-report:requested") {
      baseEvent.description = `A ${reportType ? reportType + " " : ""}condition report for <span class="${boldStyle}">${formatAddress(unitAddress)}</span> has been requested.`;

      if (eventUserType === "tenant") {
        baseEvent.action.href = `/tenant/leases/${leaseId}/condition-reports/${conditionReportId}/`;
      }

      if (eventUserType === "organization") {
        baseEvent.action.href = `/landlord/condition-reports/${conditionReportId}/`;
      }
    }

    if (event.event === "condition-report:completed") {
      baseEvent.description = `A ${reportType ? reportType + " " : ""}condition report for <span class="${boldStyle}">${formatAddress(unitAddress)}</span> has been completed.`;

      if (eventUserType === "tenant") {
        baseEvent.action.href = `/tenant/leases/${leaseId}/condition-reports/${conditionReportId}/`;
      }

      if (eventUserType === "organization") {
        baseEvent.action.href = `/landlord/condition-reports/${conditionReportId}/`;
      }
    }
  }

  if (
    event.event === "maintenance-request:created" ||
    event.event === "maintenance-request:updated"
  ) {
    if (typeof notification?.maintenanceRequest === "string") {
      return null;
    }

    if (typeof notification?.maintenanceRequest?.unit === "string") {
      return null;
    }

    if (typeof notification?.maintenanceRequest?.lease === "string") {
      return null;
    }

    const maintenanceRequestId = notification?.maintenanceRequest?.id;
    const unitAddress = notification?.maintenanceRequest?.unit?.address;

    if (event.event === "maintenance-request:created") {
      baseEvent.description = `A maintenance request for <span class="${boldStyle}">${formatAddress(unitAddress)}</span> has been created.`;

      if (eventUserType === "tenant") {
        baseEvent.action.href = `/tenant/maintenance/${maintenanceRequestId}/`;
      }

      if (eventUserType === "organization") {
        baseEvent.action.href = `/landlord/maintenance/${maintenanceRequestId}/`;
      }
    }

    if (event.event === "maintenance-request:updated") {
      baseEvent.description = `A maintenance request for <span class="${boldStyle}">${formatAddress(unitAddress)}</span> has been updated.`;

      if (eventUserType === "tenant") {
        baseEvent.action.href = `/tenant/maintenance/${maintenanceRequestId}/`;
      }

      if (eventUserType === "organization") {
        baseEvent.action.href = `/landlord/maintenance/${maintenanceRequestId}/`;
      }
    }
  }

  if (event.event === "comment:created") {
    if (typeof notification?.comment === "string") {
      return null;
    }

    if (typeof notification?.maintenanceRequest === "string") {
      return null;
    }

    if (typeof notification?.maintenanceRequest?.unit === "string") {
      return null;
    }

    if (typeof notification?.comment?.maintenanceRequest === "string") {
      return null;
    }

    const unitAddress = notification?.maintenanceRequest?.unit?.address;

    const maintenanceRequestId = notification?.comment?.maintenanceRequest?.id;

    baseEvent.description = `A new comment has been added to a maintenance request for <span class="${boldStyle}">${formatAddress(unitAddress)}</span>.`;

    if (eventUserType === "tenant") {
      baseEvent.action.href = `/tenant/maintenance/${maintenanceRequestId}/`;
    }

    if (eventUserType === "organization") {
      baseEvent.action.href = `/landlord/maintenance/${maintenanceRequestId}/`;
    }
  }

  return baseEvent;
};

export default notificationFormatter;
